import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus,
    Trash2,
    Edit3,
    Check,
    X,
    Clock,
    BarChart3,
} from 'lucide-react';
import { apiService } from '../../services/api';
import { toast } from '../UI/Toast';
import { UserAvatar } from '../UI/UserAvatar';
import type { User as UserType } from '../../types';

interface SubTaskData {
    id: string;
    parent_issue_id: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    assignee_id?: string;
    assignee?: {
        id: string;
        display_name: string;
        avatar_url: string;
    };
    start_date?: string;
    end_date?: string;
    progress: number;
    color: string;
    order_index: number;
    created_at: string;
    updated_at: string;
}

interface TimelineGanttProps {
    issueId: string;
    canEdit: boolean;
    users: UserType[];
    issueDueDate?: string;
}

const GANTT_COLORS = [
    '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981',
    '#06B6D4', '#EF4444', '#6366F1', '#14B8A6', '#F97316',
];

export const TimelineGantt: React.FC<TimelineGanttProps> = ({
    issueId,
    canEdit,
    users,
    issueDueDate,
}) => {
    const [subtasks, setSubtasks] = useState<SubTaskData[]>([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'gantt' | 'list'>('gantt');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: 'to-do',
        priority: 'medium',
        assignee_id: '',
        start_date: '',
        end_date: '',
        progress: 0,
        color: GANTT_COLORS[0],
    });

    useEffect(() => {
        loadSubtasks();
    }, [issueId]);

    const loadSubtasks = async () => {
        try {
            setLoading(true);
            setApiError(false);
            const data = await apiService.getSubTasks(issueId);
            setSubtasks(data);
        } catch (error) {
            console.error('Failed to load subtasks:', error);
            setApiError(true);
            setSubtasks([]);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            status: 'to-do',
            priority: 'medium',
            assignee_id: '',
            start_date: '',
            end_date: '',
            progress: 0,
            color: GANTT_COLORS[subtasks.length % GANTT_COLORS.length],
        });
    };

    const handleAddSubtask = async () => {
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        try {
            const payload: any = {
                title: formData.title,
                description: formData.description,
                status: formData.status,
                priority: formData.priority,
                progress: formData.progress,
                color: formData.color,
                order_index: subtasks.length,
            };
            if (formData.assignee_id) payload.assignee_id = formData.assignee_id;
            if (formData.start_date) payload.start_date = formData.start_date;
            if (formData.end_date) payload.end_date = formData.end_date;

            const newSubtask = await apiService.createSubTask(issueId, payload);
            setSubtasks((prev) => [...prev, newSubtask]);
            setShowAddForm(false);
            resetForm();
            toast.success('Sub-task added');
        } catch (error) {
            console.error('Failed to create subtask:', error);
        }
    };

    const handleUpdateSubtask = async (subtaskId: string) => {
        try {
            const payload: any = {
                title: formData.title,
                description: formData.description,
                status: formData.status,
                priority: formData.priority,
                progress: formData.progress,
                color: formData.color,
            };
            if (formData.assignee_id) payload.assignee_id = formData.assignee_id;
            else payload.assignee_id = null;
            if (formData.start_date) payload.start_date = formData.start_date;
            else payload.start_date = null;
            if (formData.end_date) payload.end_date = formData.end_date;
            else payload.end_date = null;

            const updated = await apiService.updateSubTask(issueId, subtaskId, payload);
            setSubtasks((prev) => prev.map((st) => (st.id === subtaskId ? updated : st)));
            setEditingId(null);
            resetForm();
            toast.success('Sub-task updated');
        } catch (error) {
            console.error('Failed to update subtask:', error);
        }
    };

    const handleDeleteSubtask = async (subtaskId: string) => {
        if (!window.confirm('Delete this sub-task?')) return;
        try {
            await apiService.deleteSubTask(issueId, subtaskId);
            setSubtasks((prev) => prev.filter((st) => st.id !== subtaskId));
            toast.success('Sub-task deleted');
        } catch (error) {
            console.error('Failed to delete subtask:', error);
        }
    };

    const startEditing = (subtask: SubTaskData) => {
        setEditingId(subtask.id);
        setFormData({
            title: subtask.title,
            description: subtask.description || '',
            status: subtask.status,
            priority: subtask.priority || 'medium',
            assignee_id: subtask.assignee_id || '',
            start_date: subtask.start_date || '',
            end_date: subtask.end_date || '',
            progress: subtask.progress || 0,
            color: subtask.color || GANTT_COLORS[0],
        });
        setShowAddForm(false);
    };

    const handleQuickStatusChange = async (subtask: SubTaskData, newStatus: string) => {
        try {
            const progress = newStatus === 'done' ? 100 : newStatus === 'in-progress' ? 50 : 0;
            const updated = await apiService.updateSubTask(issueId, subtask.id, {
                status: newStatus,
                progress,
            });
            setSubtasks((prev) => prev.map((st) => (st.id === subtask.id ? updated : st)));
        } catch (error) {
            console.error('Failed to update status:', error);
        }
    };

    // ─── Gantt Chart Calculations ──────────────────────────────────────────────

    const { timelineStart, totalDays, dayWidth } = useMemo(() => {
        const now = new Date();
        let minDate = new Date(now);
        let maxDate = new Date(now);
        maxDate.setDate(maxDate.getDate() + 14);

        subtasks.forEach((st) => {
            if (st.start_date) {
                const d = new Date(st.start_date);
                if (d < minDate) minDate = new Date(d);
            }
            if (st.end_date) {
                const d = new Date(st.end_date);
                if (d > maxDate) maxDate = new Date(d);
            }
        });

        if (issueDueDate) {
            const dd = new Date(issueDueDate);
            if (dd > maxDate) maxDate = new Date(dd);
        }

        minDate.setDate(minDate.getDate() - 2);
        maxDate.setDate(maxDate.getDate() + 3);

        const total = Math.max(
            Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)),
            7
        );

        return {
            timelineStart: minDate,
            timelineEnd: maxDate,
            totalDays: total,
            dayWidth: Math.max(36, 600 / total),
        };
    }, [subtasks, issueDueDate]);

    const getBarPosition = (startDate?: string, endDate?: string) => {
        if (!startDate) return { left: 0, width: 0 };
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date(start);
        end.setDate(end.getDate() + 1);

        const startOffset = Math.max(
            0,
            (start.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)
        );
        const duration = Math.max(
            1,
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
        );

        return {
            left: startOffset * dayWidth,
            width: duration * dayWidth,
        };
    };

    const dateHeaders = useMemo(() => {
        const headers: { date: Date; label: string; isToday: boolean; isWeekend: boolean }[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < totalDays; i++) {
            const d = new Date(timelineStart);
            d.setDate(d.getDate() + i);
            d.setHours(0, 0, 0, 0);
            const dayOfWeek = d.getDay();
            headers.push({
                date: d,
                label: d.getDate().toString(),
                isToday: d.getTime() === today.getTime(),
                isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
            });
        }
        return headers;
    }, [timelineStart, totalDays]);

    const monthHeaders = useMemo(() => {
        const months: { label: string; span: number }[] = [];
        let currentMonth = '';
        let currentSpan = 0;

        dateHeaders.forEach((h) => {
            const monthLabel = h.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            if (monthLabel !== currentMonth) {
                if (currentMonth) {
                    months.push({ label: currentMonth, span: currentSpan });
                }
                currentMonth = monthLabel;
                currentSpan = 1;
            } else {
                currentSpan++;
            }
        });
        if (currentMonth) {
            months.push({ label: currentMonth, span: currentSpan });
        }
        return months;
    }, [dateHeaders]);

    const overallProgress = useMemo(() => {
        if (subtasks.length === 0) return 0;
        return Math.round(subtasks.reduce((sum, st) => sum + (st.progress || 0), 0) / subtasks.length);
    }, [subtasks]);

    // ─── Render Form ───────────────────────────────────────────────────────────

    const renderSubtaskForm = (isEdit: boolean) => (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Title *</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Sub-task title"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Optional description"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                    <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="to-do">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="done">Done</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                    <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="lowest">Lowest</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="highest">Highest</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Assignee</label>
                    <select
                        value={formData.assignee_id}
                        onChange={(e) => setFormData({ ...formData, assignee_id: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Unassigned</option>
                        {users.map((u) => (
                            <option key={u.id} value={u.id}>
                                {u.displayName}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                    <div className="flex items-center space-x-1">
                        {GANTT_COLORS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setFormData({ ...formData, color: c })}
                                className={`w-6 h-6 rounded-full border-2 transition-transform ${formData.color === c ? 'border-gray-800 scale-110' : 'border-transparent'
                                    }`}
                                style={{ backgroundColor: c }}
                            />
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                    <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                    <input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        Progress: {formData.progress}%
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={formData.progress}
                        onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                </div>
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200">
                <button
                    type="button"
                    onClick={() => {
                        setShowAddForm(false);
                        setEditingId(null);
                        resetForm();
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={() => (isEdit && editingId ? handleUpdateSubtask(editingId) : handleAddSubtask())}
                    className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                    {isEdit ? 'Update' : 'Add Sub-task'}
                </button>
            </div>
        </div>
    );

    // ─── Loading ───────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="p-4 text-center">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading timeline...</p>
            </div>
        );
    }

    // ─── Hide when API failed or no subtasks (and not adding) ─────────────────
    if (apiError && subtasks.length === 0) {
        return null;
    }

    if (subtasks.length === 0 && !showAddForm) {
        return null;
    }

    // ─── Main Render ───────────────────────────────────────────────────────────

    return (
        <div className="mt-8 pt-6 border-t border-gray-200 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                        <BarChart3 className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
                    </div>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {subtasks.length} sub-task{subtasks.length !== 1 ? 's' : ''}
                    </span>
                    {subtasks.length > 0 && (
                        <div className="flex items-center space-x-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{
                                        width: `${overallProgress}%`,
                                        backgroundColor: overallProgress === 100 ? '#22C55E' : '#3B82F6',
                                    }}
                                />
                            </div>
                            <span className="text-xs text-gray-500">{overallProgress}%</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    <div className="flex bg-gray-100 rounded-md p-0.5">
                        <button
                            type="button"
                            onClick={() => setViewMode('gantt')}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'gantt'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            Gantt
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode('list')}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${viewMode === 'list'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            List
                        </button>
                    </div>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={() => {
                                resetForm();
                                setEditingId(null);
                                setShowAddForm(true);
                            }}
                            className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Add Sub-task</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Add Form */}
            {showAddForm && canEdit && renderSubtaskForm(false)}

            {/* Edit Form */}
            {editingId && canEdit && renderSubtaskForm(true)}

            {/* Empty State */}
            {subtasks.length === 0 && !showAddForm && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-1">No sub-tasks yet</p>
                    <p className="text-xs text-gray-400">
                        {canEdit
                            ? 'Add sub-tasks with timelines to track progress on a Gantt chart'
                            : 'Only assignees can add sub-tasks'}
                    </p>
                </div>
            )}

            {/* ─── Gantt View ─────────────────────────────────────────────────────── */}
            {subtasks.length > 0 && viewMode === 'gantt' && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex">
                        {/* Left panel - task names */}
                        <div className="w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50">
                            <div className="h-7 border-b border-gray-200" />
                            <div className="h-7 border-b border-gray-200 flex items-center px-3">
                                <span className="text-xs font-medium text-gray-500">Sub-task</span>
                            </div>
                            {subtasks.map((st) => (
                                <div
                                    key={st.id}
                                    className="h-10 border-b border-gray-100 flex items-center px-3 space-x-2 hover:bg-gray-100 transition-colors group"
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            canEdit &&
                                            handleQuickStatusChange(
                                                st,
                                                st.status === 'to-do'
                                                    ? 'in-progress'
                                                    : st.status === 'in-progress'
                                                        ? 'done'
                                                        : 'to-do'
                                            )
                                        }
                                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${st.status === 'done'
                                            ? 'bg-green-500 border-green-500'
                                            : st.status === 'in-progress'
                                                ? 'bg-blue-500 border-blue-500'
                                                : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        disabled={!canEdit}
                                        title={`Status: ${st.status}`}
                                    >
                                        {st.status === 'done' && <Check className="w-2.5 h-2.5 text-white" />}
                                    </button>
                                    <span
                                        className={`text-xs truncate flex-1 ${st.status === 'done' ? 'line-through text-gray-400' : 'text-gray-700'
                                            }`}
                                        title={st.title}
                                    >
                                        {st.title}
                                    </span>
                                    {st.assignee && (
                                        <UserAvatar
                                            name={st.assignee.display_name}
                                            src={st.assignee.avatar_url}
                                            className="w-5 h-5"
                                        />
                                    )}
                                    {canEdit && (
                                        <div className="hidden group-hover:flex items-center space-x-1">
                                            <button
                                                type="button"
                                                onClick={() => startEditing(st)}
                                                className="p-0.5 text-gray-400 hover:text-blue-600"
                                                title="Edit"
                                            >
                                                <Edit3 className="w-3 h-3" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteSubtask(st.id)}
                                                className="p-0.5 text-gray-400 hover:text-red-600"
                                                title="Delete"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Right panel - Gantt bars */}
                        <div className="flex-1 overflow-x-auto">
                            <div style={{ minWidth: totalDays * dayWidth }}>
                                {/* Month headers */}
                                <div className="h-7 border-b border-gray-200 flex">
                                    {monthHeaders.map((m, idx) => (
                                        <div
                                            key={idx}
                                            className="border-r border-gray-200 flex items-center justify-center"
                                            style={{ width: m.span * dayWidth }}
                                        >
                                            <span className="text-xs font-medium text-gray-600">{m.label}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Day headers */}
                                <div className="h-7 border-b border-gray-200 flex">
                                    {dateHeaders.map((h, idx) => (
                                        <div
                                            key={idx}
                                            className={`flex items-center justify-center border-r border-gray-100 ${h.isToday
                                                ? 'bg-blue-50 font-bold text-blue-700'
                                                : h.isWeekend
                                                    ? 'bg-gray-50 text-gray-400'
                                                    : 'text-gray-500'
                                                }`}
                                            style={{ width: dayWidth }}
                                        >
                                            <span className="text-[10px]">{h.label}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Task bars */}
                                {subtasks.map((st) => {
                                    const { left, width } = getBarPosition(st.start_date, st.end_date);
                                    const hasTimeline = !!st.start_date;

                                    return (
                                        <div key={st.id} className="h-10 border-b border-gray-100 relative">
                                            {/* Background grid */}
                                            <div className="absolute inset-0 flex">
                                                {dateHeaders.map((h, idx) => (
                                                    <div
                                                        key={idx}
                                                        className={`border-r border-gray-50 ${h.isToday ? 'bg-blue-50/30' : h.isWeekend ? 'bg-gray-50/50' : ''
                                                            }`}
                                                        style={{ width: dayWidth }}
                                                    />
                                                ))}
                                            </div>

                                            {/* Today line */}
                                            {dateHeaders.map(
                                                (h, idx) =>
                                                    h.isToday && (
                                                        <div
                                                            key={`today-${idx}`}
                                                            className="absolute top-0 bottom-0 w-0.5 bg-red-400 z-10"
                                                            style={{ left: idx * dayWidth + dayWidth / 2 }}
                                                        />
                                                    )
                                            )}

                                            {/* Gantt bar */}
                                            {hasTimeline && width > 0 && (
                                                <div
                                                    className="absolute top-1.5 h-7 rounded-md shadow-sm flex items-center overflow-hidden cursor-pointer hover:shadow-md transition-shadow z-20"
                                                    style={{
                                                        left,
                                                        width: Math.max(width, dayWidth * 0.5),
                                                        backgroundColor: st.color || '#3B82F6',
                                                    }}
                                                    onClick={() => canEdit && startEditing(st)}
                                                    title={`${st.title}\n${st.start_date} → ${st.end_date || st.start_date}\nProgress: ${st.progress}%`}
                                                >
                                                    {/* Progress overlay */}
                                                    <div
                                                        className="absolute inset-y-0 left-0 bg-white/20 rounded-l-md"
                                                        style={{ width: `${st.progress || 0}%` }}
                                                    />
                                                    <span className="text-[10px] text-white font-medium px-1.5 truncate relative z-10">
                                                        {st.title}
                                                    </span>
                                                </div>
                                            )}

                                            {/* No timeline indicator */}
                                            {!hasTimeline && (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-[10px] text-gray-400 italic">No dates set</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── List View ──────────────────────────────────────────────────────── */}
            {subtasks.length > 0 && viewMode === 'list' && (
                <div className="space-y-2">
                    {subtasks.map((st) => (
                        <div
                            key={st.id}
                            className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow group"
                        >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <button
                                    type="button"
                                    onClick={() =>
                                        canEdit &&
                                        handleQuickStatusChange(
                                            st,
                                            st.status === 'to-do'
                                                ? 'in-progress'
                                                : st.status === 'in-progress'
                                                    ? 'done'
                                                    : 'to-do'
                                        )
                                    }
                                    className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${st.status === 'done'
                                        ? 'bg-green-500 border-green-500'
                                        : st.status === 'in-progress'
                                            ? 'bg-blue-500 border-blue-500'
                                            : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                    disabled={!canEdit}
                                >
                                    {st.status === 'done' && <Check className="w-3 h-3 text-white" />}
                                </button>
                                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: st.color }} />
                                <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium truncate ${st.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                        {st.title}
                                    </p>
                                    <div className="flex items-center space-x-3 mt-0.5">
                                        {st.start_date && (
                                            <span className="text-[10px] text-gray-400">
                                                {st.start_date}{st.end_date ? ` → ${st.end_date}` : ''}
                                            </span>
                                        )}
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${st.priority === 'high' || st.priority === 'highest'
                                            ? 'bg-red-50 text-red-600'
                                            : st.priority === 'low' || st.priority === 'lowest'
                                                ? 'bg-blue-50 text-blue-600'
                                                : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            {st.priority}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3 ml-3">
                                {/* Progress bar */}
                                <div className="flex items-center space-x-1.5">
                                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all"
                                            style={{
                                                width: `${st.progress}%`,
                                                backgroundColor: st.progress === 100 ? '#22C55E' : st.color,
                                            }}
                                        />
                                    </div>
                                    <span className="text-[10px] text-gray-400 w-7 text-right">{st.progress}%</span>
                                </div>
                                {st.assignee && (
                                    <UserAvatar
                                        name={st.assignee.display_name}
                                        src={st.assignee.avatar_url}
                                        className="w-6 h-6"
                                    />
                                )}
                                {canEdit && (
                                    <div className="hidden group-hover:flex items-center space-x-1">
                                        <button
                                            type="button"
                                            onClick={() => startEditing(st)}
                                            className="p-1 text-gray-400 hover:text-blue-600 rounded"
                                            title="Edit"
                                        >
                                            <Edit3 className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteSubtask(st.id)}
                                            className="p-1 text-gray-400 hover:text-red-600 rounded"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};